import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

// Use relative URL for session exchange — this ensures same-origin on ANY domain
// (production sll.britehub.community, preview, etc.) and avoids CORS/load failures
const SESSION_URL = '/api/auth/session';

async function exchangeSession(sessionId, attempt = 1) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);

  try {
    const res = await fetch(SESSION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Session-ID': sessionId },
      credentials: 'include',
      signal: controller.signal
    });
    clearTimeout(timeout);

    if (!res.ok) {
      const errBody = await res.text().catch(() => '');
      throw new Error(`Server ${res.status}: ${errBody}`);
    }

    return await res.json();
  } catch (err) {
    clearTimeout(timeout);
    if (attempt < 3 && (err.name === 'AbortError' || err.name === 'TypeError')) {
      await new Promise(r => setTimeout(r, 1000 * attempt));
      return exchangeSession(sessionId, attempt + 1);
    }
    throw err;
  }
}

function storeAuthData(data) {
  if (data.session_token) {
    sessionStorage.setItem('session_token', data.session_token);
    localStorage.setItem('sll_session_token', data.session_token);
  }
  if (data.user) {
    const userStr = JSON.stringify(data.user);
    sessionStorage.setItem('sll_user', userStr);
    localStorage.setItem('sll_user', userStr);
  }
}

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Completing authentication...');
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  const processAuth = (sid) => {
    setError(null);
    setStatus('Verifying your account...');

    // Clear stale auth data
    sessionStorage.removeItem('session_token');
    sessionStorage.removeItem('sll_user');
    localStorage.removeItem('sll_session_token');
    localStorage.removeItem('sll_user');

    exchangeSession(sid)
      .then(data => {
        if (!data.session_token) {
          throw new Error('No session token received from server');
        }

        storeAuthData(data);
        setStatus('Redirecting to dashboard...');

        // Default destination — complete profile go to dashboard, otherwise
        // to /profile so they finish their setup. Overridden below if the
        // user originally tried to deep-link somewhere (e.g. a shared
        // /news?article=... link) before being bounced to login.
        let dest = data.user?.profile?.profile_completed ? '/progress' : '/profile';
        try {
          const stashed = sessionStorage.getItem('post_login_redirect');
          if (stashed && data.user?.profile?.profile_completed) {
            dest = stashed;
            sessionStorage.removeItem('post_login_redirect');
          }
        } catch (e) {}

        // Delay to ensure storage writes flush on mobile
        setTimeout(() => {
          window.location.replace(dest);
        }, 300);
      })
      .catch(err => {
        console.error('OAuth session exchange failed:', err);
        setError(err.message || 'Unknown error');
        setStatus('Sign-in failed');
      });
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sid = params.get('session_id');
    setSessionId(sid);

    if (!sid) {
      setError('No session ID found in URL');
      setStatus('Authentication failed');
      return;
    }

    processAuth(sid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="text-center max-w-sm">
        {!error ? (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-slate-600" data-testid="auth-callback-status">{status}</p>
          </>
        ) : (
          <>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left">
              <p className="text-sm font-medium text-red-800 mb-1">Sign-in failed</p>
              <p className="text-xs text-red-600 break-words">{error}</p>
            </div>
            <div className="space-y-2">
              {sessionId && (
                <button
                  onClick={() => processAuth(sessionId)}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
                  data-testid="retry-auth-btn"
                >
                  Try Again
                </button>
              )}
              <button
                onClick={() => { window.location.href = '/login'; }}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50"
                data-testid="back-to-login-btn"
              >
                Back to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
